/*
  Controller for blocks/subscription-presets.liquid.

  It does not price anything and it creates no selling plans. The compiled buy-box
  engine owns pricing, selling-plan resolution and the cart payload; this file only

    1. translates a preset tile click into clicks on the real (hidden) steppers,
       multiplied by the household size, and
    2. writes the derived strings the engine has no concept of: the per-cup chip,
       the "N cups / month, billed X every Y" line, and the savings note.

  Everything in (2) is derived from text the engine writes, so it has to re-run when
  the engine re-renders. Hence the MutationObserver -- and hence the care about what
  it observes. An early version watched the whole [data-presets] container while also
  writing into it, which fed itself and locked the page. It observes only the nodes
  the engine writes (prices, badges, stepper values) and never a node this file
  writes, and it disconnects for the duration of its own writes as a second guard.

  Two notes on the boot, both learned the hard way on this theme:

  The initialised guard is a WeakSet, not a data- attribute. This theme morphs
  elements on navigation and copies attributes across while dropping every JS
  handler, so an attribute flag says "already set up" on an element whose listeners
  are gone, and the block silently never initialises. WeakSet membership does not
  survive the morph, which is exactly the semantics wanted: new element, new setup.

  The opening selection is applied by a short poll rather than one rAF. The engine
  renders on its own schedule and the buy-box element can be replaced after this
  script first runs, so a single frame is a coin flip. The poll retries until the
  selection has visibly taken (the picked-set label is non-empty) and then stops.

  Gallery sizing is NOT handled here. assets/heist-gallery-fit.js already repairs it
  for every product page on this theme.
*/
(function () {
  // Not {pct}: a curly token cannot survive inside a Liquid output tag, and the
  // template default lives in one.
  var PCT_TOKEN = '[pct]';
  var BOOT_POLL_MS = 120;
  var BOOT_MAX_TRIES = 50; // ~6s

  var wired = new WeakSet();

  function init(root) {
    var ui = root.querySelector('[data-presets]');
    if (!ui || wired.has(root)) return;
    wired.add(root);

    // Cups per bag rather than cups per month, so the arithmetic stays correct when
    // the household size doubles the box. Everything downstream derives from the
    // bags actually in the box.
    var CUPS_PER_BAG = Number(ui.dataset.cupsPerBag) || 0;
    var DIVISOR = Number(ui.dataset.divisor) || 1;
    var NOTE_TPL = ui.dataset.noteTemplate || '';

    var people = 1;

    /* ---- tiles drive the hidden steppers ---- */

    // Synthetic clicks rather than setting .textContent: the engine keeps its own
    // quantity map and only updates it from its click handlers, so writing the
    // visible number directly would desync the two.
    function stepTo(variantId, target) {
      var st = root.querySelector('[data-buy-box-stepper][data-variant-id="' + variantId + '"]');
      if (!st) return;
      var valueEl = st.querySelector('[data-stepper-value]');
      if (!valueEl) return;
      var inc = st.querySelector('[data-stepper-increase]');
      var dec = st.querySelector('[data-stepper-decrease]');
      var read = function () { return parseInt(valueEl.textContent, 10) || 0; };
      var guard = 0;
      while (read() < target && guard++ < 50) { if (!inc || inc.disabled) break; inc.click(); }
      guard = 0;
      while (read() > target && guard++ < 50) { if (!dec || dec.disabled) break; dec.click(); }
    }

    // Zero the unwanted variants first, then raise the wanted ones. Either order ends
    // in the same place, but this one leaves the engine's media stack with the chosen
    // coffee on top, which is what swaps the PDP gallery.
    function applyPreset(spec) {
      var wanted = {};
      String(spec || '').split(',').forEach(function (pair) {
        var p = pair.split(':');
        if (p.length === 2) wanted[p[0].trim()] = (parseInt(p[1], 10) || 0) * people;
      });
      root.querySelectorAll('[data-buy-box-stepper]').forEach(function (st) {
        if (!(st.dataset.variantId in wanted)) stepTo(st.dataset.variantId, 0);
      });
      Object.keys(wanted).forEach(function (id) { stepTo(id, wanted[id]); });
    }

    function activeTile() {
      return (
        ui.querySelector('[data-preset-tile][aria-pressed="true"]') ||
        ui.querySelector('[data-preset-tile]')
      );
    }

    function selectTile(tile) {
      ui.querySelectorAll('[data-preset-tile]').forEach(function (t) {
        t.setAttribute('aria-pressed', String(t === tile));
      });
      var pick = ui.querySelector('[data-presets-pick]');
      if (pick) pick.innerHTML = tile.dataset.name || '';
      applyPreset(tile.dataset.spec);
    }

    // Household size is a multiplier on the chosen set, not a set of its own, so it
    // just re-applies whatever tile is active.
    function setPeople(next) {
      if (!(next > 0) || next === people) return;
      people = next;
      ui.querySelectorAll('[data-people]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(Number(b.dataset.people) === people));
      });
      var tile = activeTile();
      if (tile) applyPreset(tile.dataset.spec);
    }

    ui.addEventListener('click', function (e) {
      var person = e.target.closest('[data-people]');
      if (person) {
        setPeople(parseInt(person.dataset.people, 10) || 1);
        return;
      }
      var tile = e.target.closest('[data-preset-tile]');
      if (tile) selectTile(tile);
    });

    /* ---- derived text ---- */

    function num(t) {
      var n = Number(String(t || '').replace(/[^0-9.]/g, ''));
      return isFinite(n) ? n : 0;
    }
    function money(n) { return '$' + n.toFixed(2); }
    function put(el, text) { if (el && el.textContent !== text) el.textContent = text; }

    var observer;

    function syncDerived() {
      if (observer) observer.disconnect();
      try {
        var bags = 0;
        root.querySelectorAll('[data-stepper-value]').forEach(function (v) {
          bags += parseInt(v.textContent, 10) || 0;
        });

        // Cups, not bags. A bag is packaging; a cup is the thing being bought, and
        // it is the same unit as the per-cup price directly beside it.
        //
        // Per MONTH on both cards, never per prepaid term. The price alongside it is
        // a monthly price, so a 3-month total here would pair a 3-month quantity with
        // a 1-month price and read as a far worse deal than it is. The box is the
        // same size on both plans -- only the billing differs, which is exactly the
        // comparison the two cards exist to make.
        var cupsPerMonth = Math.round(bags * CUPS_PER_BAG);

        ui.querySelectorAll('[data-buy-box-tier]').forEach(function (card) {
          var priceEl = card.querySelector('[data-tier-price]');
          var monthly = num(priceEl ? priceEl.textContent : '');
          var prepaid = card.dataset.tier === 'heist';

          var cup = card.querySelector('[data-cup]');
          if (cup) {
            if (monthly > 0 && cupsPerMonth > 0) {
              put(cup, money(monthly / cupsPerMonth) + ' a cup');
              cup.hidden = false;
            } else {
              cup.hidden = true;
            }
          }

          // Billing cadence is a charge authorisation, so it states the real Skio
          // interval, not a rounded one. The prepaid plan bills every 90 days. The
          // monthly plan is "Ships every 4 weeks" -- 28 days, not 30, which is 13.04
          // cycles a year rather than 12. The cups figure above stays a per-month
          // supply rate on both cards so the two plans stay comparable.
          var meta = card.querySelector('[data-meta]');
          if (meta) {
            if (monthly <= 0 || bags <= 0) {
              put(meta, '');
            } else if (prepaid) {
              put(
                meta,
                cupsPerMonth + ' cups / month · billed ' + money(monthly * DIVISOR) +
                  ' every ' + 30 * DIVISOR + ' days'
              );
            } else {
              put(
                meta,
                cupsPerMonth + ' cups / month · billed ' + money(monthly) + ' every 4 weeks'
              );
            }
          }
        });

        // The savings figure is read back off the engine's own discount badge rather
        // than typed into a setting, so the note can never advertise a discount the
        // plans do not actually give.
        var noteEl = ui.querySelector('[data-plans-note]');
        if (noteEl && NOTE_TPL) {
          var heist = ui.querySelector('[data-buy-box-tier][data-tier="heist"]');
          var badge = heist && heist.querySelector('[data-tier-badge]');
          var pct = badge ? String(badge.textContent || '').replace(/[^0-9]/g, '') : '';
          put(noteEl, pct ? NOTE_TPL.split(PCT_TOKEN).join(pct) : '');
        }
      } finally {
        if (observer) watch();
      }
    }

    // Only nodes the ENGINE writes. Never a node syncDerived writes.
    function watch() {
      var opts = { childList: true, characterData: true, subtree: true };
      root.querySelectorAll('[data-tier-price]').forEach(function (el) { observer.observe(el, opts); });
      root.querySelectorAll('[data-tier-badge]').forEach(function (el) { observer.observe(el, opts); });
      root.querySelectorAll('[data-stepper-value]').forEach(function (el) { observer.observe(el, opts); });
    }

    observer = new MutationObserver(syncDerived);
    watch();

    // Apply the opening selection, retrying until it visibly takes. See the header:
    // one frame is not enough on this theme.
    var tries = 0;
    var opening = setInterval(function () {
      tries++;
      if (!root.isConnected) { clearInterval(opening); return; }
      var pick = ui.querySelector('[data-presets-pick]');
      var first = activeTile();
      if (first && pick && !pick.textContent.trim()) {
        selectTile(first);
        syncDerived();
      }
      if ((pick && pick.textContent.trim()) || tries >= BOOT_MAX_TRIES) clearInterval(opening);
    }, BOOT_POLL_MS);
  }

  function boot() {
    document.querySelectorAll('buy-box[data-variation="presets"]').forEach(init);
  }

  // Poll briefly as well as on ready: the block can be rendered or replaced after
  // this script first runs.
  function watchForBlock() {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      boot();
      if (tries >= BOOT_MAX_TRIES) clearInterval(t);
    }, BOOT_POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); watchForBlock(); });
  } else {
    boot();
    watchForBlock();
  }
})();
